import React from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { ChapterActivity } from "@/api/fetchChapterActivities/types";
import { formatDistance } from "date-fns";

/**
 * A modal to view the details of a ChapterActivity
 */
interface ChapterActivityDetailsProps {
  isOpen: boolean;
  activity: ChapterActivity | null;
  onClose: () => void;
}

const ChapterActivityDetails: React.FC<ChapterActivityDetailsProps> = ({
  isOpen,
  activity,
  onClose,
}) => {
  if (!isOpen || !activity) return null;

  // Format date for display
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  // Format relative time
  const getRelativeTime = (dateString?: string | null) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      return formatDistance(date, new Date(), { addSuffix: true });
    } catch (e) {
      return "";
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-50">
      <div className="relative mx-auto flex min-h-full w-full max-w-3xl flex-col bg-white p-6 rounded-lg mt-10 mb-10">
        {/* Header */}
        <div className="flex items-center justify-between border-b pb-4">
          <h2 className="text-xl font-bold">
            {activity.chapterActivityName || "Chapter Activity"}
          </h2>
          <Button onClick={onClose} size="sm" variant="ghost">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-grow overflow-auto py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-semibold mb-3">Activity Details</h3>

              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-500">Activity Type</p>
                  <p className="font-medium">
                    {activity.activityType || "N/A"}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="font-medium">
                    {formatDate(activity.chapterActivityDate)}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Advisor Present</p>
                  <p className="font-medium">
                    {activity.advisorPresent === "true" ? "Yes" : "No"}
                  </p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">NSCS Members</p>
                  <p className="font-medium">{activity.numOfNSCSMembers}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Non-NSCS Members</p>
                  <p className="font-medium">{activity.numOfNonNSCSMembers}</p>
                </div>

                {activity.otherCampusOrgsInvolved && (
                  <div>
                    <p className="text-sm text-gray-500">
                      Other Organizations Involved
                    </p>
                    <p className="font-medium">
                      {activity.otherCampusOrgsInvolved}
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3">Description</h3>
              <div className="bg-gray-50 p-4 rounded-lg min-h-[200px]">
                {activity.description || "No description provided."}
              </div>

              <div className="mt-6 text-sm text-gray-500">
                <p>
                  Activity ID:{" "}
                  {activity.hubspotCreateDate
                    ? `#${activity.id}`
                    : "Not yet assigned"}
                </p>
                {activity.createdAt && (
                  <p>Submitted: {getRelativeTime(activity.createdAt)}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChapterActivityDetails;
